import {
  emptyHistoryPhysicalExamClinicalInfo,
  emptyPathologySurgicalFindingsClinicalInfo,
  HistoryPhysicalExamClinicalInfo,
  PathologySurgicalFindingsClinicalInfo,
} from "../../services/models/sccClinicalSection.model";

const IMMUNOSUPPRESSION_VALUE = "Immuno-suppressed patient";
const PERINEURAL_VALUE = "Perineural invasion";
const POORLY_DIFFERENTIATED_VALUE = "Poorly differentiated";
const TUMOR_SIZE_VALUE = "≥2cm size (largest diameter)";
const LOCATED_H_M_VALUE = "Located on Areas H or M";

const LEGACY_DEPTH_VALUE = "Invasion beyond subcutaneous fat";
const FULL_DEPTH_VALUE =
  "Depth: Invasive beyond subcutaneous fat or ≥Clark Level IV or >2mm";

const HISTORY_MIGRATED_VALUES = new Set([
  IMMUNOSUPPRESSION_VALUE,
  TUMOR_SIZE_VALUE,
  LOCATED_H_M_VALUE,
]);

const PATHOLOGY_MIGRATED_VALUES = new Set([
  PERINEURAL_VALUE,
  POORLY_DIFFERENTIATED_VALUE,
]);

type SccRequiredFields = Partial<
  HistoryPhysicalExamClinicalInfo & PathologySurgicalFindingsClinicalInfo
>;

type SectionWithNestedRequired = {
  required?: SccRequiredFields | null;
  additionalHistoryPhysicalExam?: string[];
  additionalPathologySurgicalFindings?: string[];
};

function hasMeaningfulRequiredFields(
  required?: SccRequiredFields | null
): boolean {
  if (!required || typeof required !== "object") return false;
  return Object.values(required).some(
    (value) => value !== undefined && value !== null && value !== ""
  );
}

function isStructuredHistoryPhysicalExam(
  value: unknown
): value is HistoryPhysicalExamClinicalInfo {
  return (
    value != null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    "isPatientImmunocompromised" in value
  );
}

function isStructuredPathologySurgicalFindings(
  value: unknown
): value is PathologySurgicalFindingsClinicalInfo {
  return (
    value != null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    "isPerineuralInvolvement" in value
  );
}

function isSectionWithNestedRequired(
  value: unknown
): value is SectionWithNestedRequired {
  return (
    value != null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    "required" in value &&
    value.required != null &&
    typeof value.required === "object"
  );
}

function normalizePathologyAdditionalValue(value: string): string {
  if (value === LEGACY_DEPTH_VALUE) return FULL_DEPTH_VALUE;
  return value;
}

function mergeUniqueStringArrays(
  ...sources: (string[] | undefined | null)[]
): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const source of sources) {
    if (!Array.isArray(source)) continue;
    for (const item of source) {
      const normalized = normalizePathologyAdditionalValue(item);
      if (!seen.has(normalized)) {
        seen.add(normalized);
        result.push(normalized);
      }
    }
  }

  return result;
}

function mapLegacyHistoryArray(
  history: string[],
  result: HistoryPhysicalExamClinicalInfo
): void {
  const remaining = [...history];

  if (remaining.includes(IMMUNOSUPPRESSION_VALUE)) {
    result.isPatientImmunocompromised = "Yes";
    remaining.splice(remaining.indexOf(IMMUNOSUPPRESSION_VALUE), 1);
  }

  if (remaining.includes(TUMOR_SIZE_VALUE)) {
    result.tumorDiameter = "≥4 cm";
    remaining.splice(remaining.indexOf(TUMOR_SIZE_VALUE), 1);
  }

  if (remaining.includes(LOCATED_H_M_VALUE)) {
    result.additionalHistoryPhysicalExam.push(LOCATED_H_M_VALUE);
    remaining.splice(remaining.indexOf(LOCATED_H_M_VALUE), 1);
  }

  result.additionalHistoryPhysicalExam.push(
    ...remaining.filter((item) => !HISTORY_MIGRATED_VALUES.has(item))
  );
}

function mapLegacyPathologyArray(
  pathology: string[],
  result: PathologySurgicalFindingsClinicalInfo
): void {
  const remaining = [...pathology];

  if (remaining.includes(PERINEURAL_VALUE)) {
    result.isPerineuralInvolvement = "Yes";
    remaining.splice(remaining.indexOf(PERINEURAL_VALUE), 1);
  }

  if (remaining.includes(POORLY_DIFFERENTIATED_VALUE)) {
    result.isPoorlyDifferentiated = "Yes";
    remaining.splice(remaining.indexOf(POORLY_DIFFERENTIATED_VALUE), 1);
  }

  result.additionalPathologySurgicalFindings.push(
    ...remaining
      .filter((item) => !PATHOLOGY_MIGRATED_VALUES.has(item))
      .map(normalizePathologyAdditionalValue)
  );
}

function normalizeHistoryFromApi(
  value: HistoryPhysicalExamClinicalInfo | string[] | SectionWithNestedRequired | null | undefined
): HistoryPhysicalExamClinicalInfo {
  const history = emptyHistoryPhysicalExamClinicalInfo();
  if (!value) return history;

  if (isStructuredHistoryPhysicalExam(value)) {
    history.isPatientImmunocompromised =
      value.isPatientImmunocompromised ?? "";
    history.isTumorLocatedOnHeadOrNeck =
      value.isTumorLocatedOnHeadOrNeck ?? "";
    history.tumorDiameter = value.tumorDiameter ?? "";
    history.additionalHistoryPhysicalExam =
      value.additionalHistoryPhysicalExam ?? [];
    return history;
  }

  if (isSectionWithNestedRequired(value)) {
    if (hasMeaningfulRequiredFields(value.required)) {
      history.isPatientImmunocompromised =
        value.required?.isPatientImmunocompromised ?? "";
      history.isTumorLocatedOnHeadOrNeck =
        value.required?.isTumorLocatedOnHeadOrNeck ?? "";
      history.tumorDiameter = value.required?.tumorDiameter ?? "";
    }
    history.additionalHistoryPhysicalExam =
      value.additionalHistoryPhysicalExam ?? [];
    return history;
  }

  if (Array.isArray(value)) {
    mapLegacyHistoryArray(value, history);
  }

  return history;
}

function normalizePathologyFromApi(
  value:
    | PathologySurgicalFindingsClinicalInfo
    | string[]
    | SectionWithNestedRequired
    | null
    | undefined
): PathologySurgicalFindingsClinicalInfo {
  const pathology = emptyPathologySurgicalFindingsClinicalInfo();
  if (!value) return pathology;

  if (isStructuredPathologySurgicalFindings(value)) {
    pathology.isPerineuralInvolvement = value.isPerineuralInvolvement ?? "";
    pathology.isPoorlyDifferentiated = value.isPoorlyDifferentiated ?? "";
    pathology.additionalPathologySurgicalFindings = (
      value.additionalPathologySurgicalFindings ?? []
    ).map(normalizePathologyAdditionalValue);
    return pathology;
  }

  if (isSectionWithNestedRequired(value)) {
    if (hasMeaningfulRequiredFields(value.required)) {
      pathology.isPerineuralInvolvement =
        value.required?.isPerineuralInvolvement ?? "";
      pathology.isPoorlyDifferentiated =
        value.required?.isPoorlyDifferentiated ?? "";
    }
    pathology.additionalPathologySurgicalFindings = (
      value.additionalPathologySurgicalFindings ?? []
    ).map(normalizePathologyAdditionalValue);
    return pathology;
  }

  if (Array.isArray(value)) {
    mapLegacyPathologyArray(value, pathology);
  }

  return pathology;
}

function historyScalarsAreEmpty(history: HistoryPhysicalExamClinicalInfo): boolean {
  return (
    !history.isPatientImmunocompromised &&
    !history.isTumorLocatedOnHeadOrNeck &&
    !history.tumorDiameter
  );
}

function pathologyScalarsAreEmpty(
  pathology: PathologySurgicalFindingsClinicalInfo
): boolean {
  return (
    !pathology.isPerineuralInvolvement && !pathology.isPoorlyDifferentiated
  );
}

export function normalizeSccClinicalSectionsFromApi(data: {
  historyPhysicalExam?:
    | HistoryPhysicalExamClinicalInfo
    | string[]
    | SectionWithNestedRequired
    | null;
  pathologySurgicalFindings?:
    | PathologySurgicalFindingsClinicalInfo
    | string[]
    | SectionWithNestedRequired
    | null;
  sccClinicalInfo?: {
    required?: SccRequiredFields;
    additionalHistoryPhysicalExam?: string[];
    additionalPathologySurgicalFindings?: string[];
  } | null;
} | null | undefined): {
  historyPhysicalExam: HistoryPhysicalExamClinicalInfo;
  pathologySurgicalFindings: PathologySurgicalFindingsClinicalInfo;
} {
  const history = normalizeHistoryFromApi(data?.historyPhysicalExam);
  const pathology = normalizePathologyFromApi(data?.pathologySurgicalFindings);
  const nested = data?.sccClinicalInfo;

  if (hasMeaningfulRequiredFields(nested?.required)) {
    if (historyScalarsAreEmpty(history)) {
      history.isPatientImmunocompromised =
        nested?.required?.isPatientImmunocompromised ?? "";
      history.isTumorLocatedOnHeadOrNeck =
        nested?.required?.isTumorLocatedOnHeadOrNeck ?? "";
      history.tumorDiameter = nested?.required?.tumorDiameter ?? "";
    }

    if (pathologyScalarsAreEmpty(pathology)) {
      pathology.isPerineuralInvolvement =
        nested?.required?.isPerineuralInvolvement ?? "";
      pathology.isPoorlyDifferentiated =
        nested?.required?.isPoorlyDifferentiated ?? "";
    }
  }

  history.additionalHistoryPhysicalExam = mergeUniqueStringArrays(
    history.additionalHistoryPhysicalExam,
    nested?.additionalHistoryPhysicalExam,
    isSectionWithNestedRequired(data?.historyPhysicalExam)
      ? data.historyPhysicalExam.additionalHistoryPhysicalExam
      : undefined,
    isStructuredHistoryPhysicalExam(data?.historyPhysicalExam)
      ? data.historyPhysicalExam.additionalHistoryPhysicalExam
      : undefined
  );

  pathology.additionalPathologySurgicalFindings = mergeUniqueStringArrays(
    pathology.additionalPathologySurgicalFindings,
    nested?.additionalPathologySurgicalFindings,
    isSectionWithNestedRequired(data?.pathologySurgicalFindings)
      ? data.pathologySurgicalFindings.additionalPathologySurgicalFindings
      : undefined,
    isStructuredPathologySurgicalFindings(data?.pathologySurgicalFindings)
      ? data.pathologySurgicalFindings.additionalPathologySurgicalFindings
      : undefined
  );

  return { historyPhysicalExam: history, pathologySurgicalFindings: pathology };
}

export function sccClinicalSectionsToLegacyOptionArrays(sections: {
  historyPhysicalExam: HistoryPhysicalExamClinicalInfo;
  pathologySurgicalFindings: PathologySurgicalFindingsClinicalInfo;
}): {
  historyPhysicalExam: string[];
  pathologySurgicalFindings: string[];
} {
  const history = [
    ...sections.historyPhysicalExam.additionalHistoryPhysicalExam,
  ];
  const pathology = [
    ...sections.pathologySurgicalFindings.additionalPathologySurgicalFindings,
  ];
  const { historyPhysicalExam, pathologySurgicalFindings } = sections;

  if (historyPhysicalExam.isPatientImmunocompromised === "Yes") {
    history.push(IMMUNOSUPPRESSION_VALUE);
  }
  if (
    historyPhysicalExam.tumorDiameter === "2 to <4cm" ||
    historyPhysicalExam.tumorDiameter === "≥4 cm"
  ) {
    history.push(TUMOR_SIZE_VALUE);
  }
  if (historyPhysicalExam.isTumorLocatedOnHeadOrNeck === "Yes") {
    history.push(LOCATED_H_M_VALUE);
  }
  if (pathologySurgicalFindings.isPerineuralInvolvement === "Yes") {
    pathology.push(PERINEURAL_VALUE);
  }
  if (pathologySurgicalFindings.isPoorlyDifferentiated === "Yes") {
    pathology.push(POORLY_DIFFERENTIATED_VALUE);
  }

  return { historyPhysicalExam: history, pathologySurgicalFindings: pathology };
}
